import { differenceInCalendarDays } from "date-fns";
import type { Patient, Treatment } from "@akina/domain";
import type { PrescriptionStatusEnum } from "types/schemas";
import { Exceptions } from "@akina/infra/exceptions";

const LOCKED_OUT_PRESCRIPTION_STATUSES: PrescriptionStatusEnum[] = [
  "pending",
  "under_review",
  "rejected",
];

const LOCKED_OUT_THRESHOLD_DAYS = 14;
const EXPIRED_PATIENT_STATES = new Set<Patient["state"]>(["completed", "detached", "passive"]);

export function checkInvalidPrescriptionState(
  treatment: { prescriptionStatus: PrescriptionStatusEnum; startDate: Date | string } | null | undefined,
): boolean {
  if (treatment == null) return false;
  if (!LOCKED_OUT_PRESCRIPTION_STATUSES.includes(treatment.prescriptionStatus)) return false;
  const startDate = typeof treatment.startDate === "string" ? new Date(treatment.startDate) : treatment.startDate;
  if (isNaN(startDate.getTime())) return false;
  return differenceInCalendarDays(new Date(), startDate) > LOCKED_OUT_THRESHOLD_DAYS;
}

export function checkPrescriptionExpired(patient: Patient | undefined, error: Error | null): boolean {
  return error instanceof Exceptions[404] || (patient != null && EXPIRED_PATIENT_STATES.has(patient.state));

}

export const isPrescriptionPending = (treatment: Treatment): boolean =>
  treatment.prescriptionStatus === "pending" || treatment.nextPrescriptionStatus === "pending";

export const isExtensionOnly = (treatment: Treatment): boolean =>
  treatment.prescriptionStatus !== "pending" && treatment.nextPrescriptionStatus === "pending";

type PrescriptionTreatmentSummary = {
  treatmentId: string;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string | null;
  age?: number;
};

export function buildPrescriptionTreatmentSummary(
  patient: {
    firstName: string;
    lastName: string;
    profilePictureUrl?: string | null;
    age?: number;
  },
  treatment: { id: string },
): PrescriptionTreatmentSummary {
  return {
    treatmentId: treatment.id,
    firstName: patient.firstName,
    lastName: patient.lastName,
    profilePictureUrl: patient.profilePictureUrl,
    ...(patient.age != null && { age: patient.age }),
  };
}
